import {useRef} from 'react';
import {useLocation} from 'react-router-dom';

/**
 * O clique numa notificação como navegação: toda tela aberta por uma notificação (relatório pronto,
 * evento, sinistro) recebe, no estado da localização, um valor novo a cada clique. A tela depende
 * desse valor, e não da URL nem de `location.key`, para refazer a seleção quando o destino já está
 * aberto (mesma URL) sem reagir a outras mudanças de localização, como o `replace` que fecha um
 * modal ou troca o hash do mapa.
 *
 * CONSTRAINT: todo produtor de navegação por notificação grava este estado: `NotificationLink`,
 * o sino (`event-button-box`) e as linhas de sinistro.
 */

let clicks = 0;

/**
 * @return {{notificationClick: string}} estado para `history.push`/`replace`; nunca repete um valor
 */
export const notificationClickState = () => ({notificationClick: `${Date.now()}-${++clicks}`});

/**
 * @param {Object} location localização do react-router
 * @return {string|null} o valor do clique que trouxe esta localização, ou `null` se não veio de notificação
 */
export const readNotificationClick = (location) =>
  (location && location.state && location.state.notificationClick) || null;

/**
 * O último valor de clique visto pela tela, para ela reagir só a um clique novo. Uma localização sem
 * valor (um `replace` que apagou o estado, como o que fecha um modal ou troca o hash) não conta como
 * mudança: o valor anterior continua valendo.
 *
 * @return {string|null} o valor do último clique em notificação, ou `null` se nenhum chegou ainda
 */
export const useNotificationClick = () => {
  const last = useRef(null);
  const current = readNotificationClick(useLocation());
  if (current !== null) {
    last.current = current;
  }
  return last.current;
};
